import { abbrNum, getMetricValueInPercentage, isDefined } from './helper/common';
import { isArray, isNil, isObject } from 'lodash';

import { CHANNELS_WITH_METRICS, MIN_FOLLOWERS_COUNT_FOR_MAIN_CHANNEL } from 'utils/channels';
import { isMetricsStillFetching } from 'utils/helper/profile';

import type {
  Channel,
  ChannelMetric,
  ChannelTypes,
  Channels,
  Metric,
  ReportMetric,
  StatisticsCountry,
  Subcampaign,
  SubcampaignInfluencerProfile
} from 'types';
import type { ContentCardCreatorProfile } from 'components/cards/content-card/types';
import type { MetricsChannelsBasicItem } from 'components/metrics/channels-basic/types';
import type { MetricsChannelsAdvancedItem } from 'components/metrics/channels-advanced/types';

type MetricItem<T> = {
  name: string,
  value: T
};

type ChannelAsObject = {
  [key: string]: {
    type: string,
    username: string | undefined | null,
    url: string,
    metrics?: Array<Metric>
  }
};

type ChannelAsArray = Array<Channel>;

export function getCountMetric(value?: number | null, useHyphenWhenNull?: boolean | null): string | null {
  if (isNil(value) && useHyphenWhenNull) {
    return '-';
  }

  if (isNil(value) || value < 0) {
    return null;
  }

  return abbrNum(value, { decimal: 1 }, true);
}

export function getRateMetric(value?: number | null): string | null {
  if (isNil(value) || value < 0) {
    return null;
  }

  let newValue = '0.0';

  if (value !== 0) {
    newValue = value < 0.01 ? '<0.01' : value.toFixed(2);
  }

  if (value >= 0.1) {
    return getMetricValueInPercentage(value);
  }

  return `${newValue}%`;
}

export function findMetricValue<T>(metrics: Array<MetricItem<T>>, name: string): T | undefined {
  const metricValue = metrics.find(metric => metric.name === name);

  return metricValue && metricValue.value;
}

function getChannelMetrics(type, channel, metrics) {
  const engagement = type === 'array' ? findMetricValue(metrics, 'engagement') : metrics.engagement;
  const followers = type === 'array' ? findMetricValue(metrics, 'num_followers') : metrics.followers;

  return {
    type: channel.type,
    username: channel.username,
    url: channel.url,
    engagement,
    followers
  };
}

export function getChannelsMetrics(channels?: Channels | Partial<ChannelAsObject> | ChannelAsArray | null): Array<ChannelMetric> | null {
  if (isNil(channels)) {
    return null;
  }

  const metricData: Array<ChannelMetric> = [];

  if (isArray(channels)) {
    channels.forEach(channel => {
      const typeOfMetrics = isArray(channel.metrics) ? 'array' : 'object';
      const metrics = getChannelMetrics(typeOfMetrics, channel, channel.metrics);
      const isStillFetchingMetrics = isMetricsStillFetching(metrics);
      const followers = metrics?.followers ?? 0;
      const canInclude = followers >= MIN_FOLLOWERS_COUNT_FOR_MAIN_CHANNEL
        || isStillFetchingMetrics;

      if (CHANNELS_WITH_METRICS.includes(channel.type) && canInclude) {
        metricData.push(metrics as any);
      }
    });
  }

  if (!isArray(channels) && isObject(channels)) {
    CHANNELS_WITH_METRICS.forEach(channel => {
      const currentChannel = channels[channel];

      if (!isNil(currentChannel)) {
        const metrics = (currentChannel && currentChannel.metrics) || {};
        const typeOfMetrics = isArray(metrics) ? 'array' : 'object';
        const channelMetrics = getChannelMetrics(typeOfMetrics, currentChannel, metrics);
        const isStillFetchingMetrics = isMetricsStillFetching(channelMetrics);
        const followers = channelMetrics?.followers ?? 0;
        const canInclude = followers >= MIN_FOLLOWERS_COUNT_FOR_MAIN_CHANNEL
          || isStillFetchingMetrics;

        if (canInclude) {
          metricData.push(channelMetrics as any);
        }
      }
    });
  }

  return metricData;
}

export function sortChannelMetrics<T extends {
  type: string
}>(channels: Array<T> | null): Array<T> | null {
  if (channels === null) {
    return null;
  }

  const channelWeights = {
    instagram: 0,
    tiktok: 1,
    youtube: 2
  };

  return channels.slice().sort((a, b) => (
    channelWeights[a.type] - channelWeights[b.type]
  ));
}

export function getMoneyMetric(value?: number | null): string | null {
  if (isNil(value) || value < 0) {
    return null;
  }

  return String(value);
}

export function getTimeMetric(value?: number | null): string | null {
  if (isNil(value) || value < 0) {
    return null;
  }

  return `${value} sec`;
}

export function getAudienceInfo(statistic: StatisticsCountry) {
  if (!statistic) {
    return '-';
  }

  return `${statistic.code || ''} (${abbrNum(statistic.reach, { decimal: 0 })} | ${Math.round(statistic.percent)}%)`;
}

type MetricsChannelAgregated = MetricsChannelsBasicItem & {
  followers: number,
  engagementTotal: number,
  influencersTotal: number
};

export const prepareAggregatedMetricsData = (subcampaigns: Array<Subcampaign>, channelType: ChannelTypes): MetricsChannelsBasicItem => (
  subcampaigns.reduce((channel: MetricsChannelAgregated, s: Subcampaign) => {
    const metrics = s.creatorProfile.channels[channelType]?.metrics ?? null;

    /* eslint-disable no-param-reassign */
    channel.followers += metrics?.followers ?? 0;
    channel.engagementTotal += metrics?.engagement || 0;
    channel.influencersTotal += metrics ? 1 : 0;
    channel.engagementRate = channel.engagementTotal / channel.influencersTotal;
    /* eslint-disable no-param-reassign */

    return channel;
  }, {
    type: channelType,
    url: '',
    followers: 0,
    engagementRate: 0,
    engagementTotal: 0,
    influencersTotal: 0
  })
);

export const prepareDataForMetricsChannelsBasicFromCreator = (
  creatorProfile: Partial<ContentCardCreatorProfile|SubcampaignInfluencerProfile>,
  channel: ChannelTypes
): MetricsChannelsBasicItem => {
  // @ts-ignore here Metrics is an Array
  const channels = creatorProfile?.metrics || [];
  const activeChannel = channels.find(item => item.type === channel);
  const metrics = activeChannel || {};

  return {
    engagementRate: metrics.engagement ?? 0,
    followers: metrics.followers ?? 0,
    type: channel,
    url: activeChannel?.url || ''
  };
};

// use this function when using `MetricsChannelBasic` component
export const prepareDataForMetricsChannelsBasic = (
  creatorProfile: Partial<ContentCardCreatorProfile|SubcampaignInfluencerProfile>,
  channel: ChannelTypes
): MetricsChannelsBasicItem => {
  const channels = creatorProfile?.channels || [];
  // to align receiving data from Array or Object
  const activeChannel = Object.values(channels).filter(isDefined).find(item => item?.type === channel);
  const metrics = (activeChannel?.metrics || {}) as Metric;

  return {
    // @ts-ignore here Metrics is an Object
    engagementRate: (metrics.engagementRate || metrics.engagement),
    // @ts-ignore here Metrics is an Object
    followers: metrics.followers,
    type: channel,
    url: activeChannel?.url || ''
  };
};

// use when implementing `MetricsChannelAdvanced`
export const prepareDataForMetricsChannelsAdvanced = (
  creatorProfile: Partial<ContentCardCreatorProfile|SubcampaignInfluencerProfile>,
  channel: ChannelTypes,
  metrics: ReportMetric
): MetricsChannelsAdvancedItem => {
  const channels = creatorProfile?.channels || [];
  // @ts-ignore here channels is an Array
  const activeChannel = channels.find(item => item.type === channel);

  return {
    currentEngagementRate: metrics.currentEngagementRate ?? 0,
    currentFollowers: metrics.currentFollowers ?? 0,
    engagementRate: metrics.engagementRate ?? 0,
    followers: metrics.followers ?? 0,
    offerDateEngagementRate: metrics.offerDateEngagementRate ?? 0,
    offerDateFollowers: metrics.offerDateFollowers ?? 0,
    postDateEngagementRate: metrics.postDateEngagementRate ?? 0,
    postDateFollowers: metrics.postDateFollowers ?? 0,
    type: channel,
    url: activeChannel?.url || ''
  };
};
